import { useState, useMemo } from 'react';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';
import { ComponentPrioritySchema } from '@youshift/shared/hooks/mutations';
import {
  dateToString,
  formatUserName,
  returnColor,
} from '@youshift/shared/utils';
import { useTranslation } from 'react-i18next';
import {
  AlgComponentType,
  Section,
  SectionSlot,
  ShiftAssignment,
  ShiftAssignmentType,
  User,
} from '@youshift/shared/types';

import { useItrContext } from '../../layouts/IterationRootLayout/IterationRootLayout';
import ImportanceValueInput from './ImportanceValueInput';
import { IconDisplay } from '../../utils/calendar';
import { getImportanceLevel, getImportanceValue, isUnbreakable } from './utils';
import ImportanceLevelListbox from './ImportanceLevelListbox';

interface ShiftTypeStyle {
  borderColor: string;
  backgroundColor: string;
  textColor: string;
  translationKey: string;
}

const SHIFT_TYPE_STYLES: Record<ShiftAssignmentType, ShiftTypeStyle> = {
  [ShiftAssignmentType.MANAGER]: {
    borderColor: '#3B82F6', // blue-500
    backgroundColor: '#EFF6FF', // blue-50
    textColor: '#1E40AF', // blue-800
    translationKey: `generic.assignedBy${ShiftAssignmentType.MANAGER}`,
  },
  [ShiftAssignmentType.USER]: {
    borderColor: '#10B981', // emerald-500
    backgroundColor: '#ECFDF5', // emerald-50
    textColor: '#065F46', // emerald-800
    translationKey: `generic.assignedBy${ShiftAssignmentType.USER}`,
  },
  [ShiftAssignmentType.ALGORITHM]: {
    borderColor: '#8B5CF6', // violet-500
    backgroundColor: '#F5F3FF', // violet-50
    textColor: '#5B21B6', // violet-800
    translationKey: `generic.assignedBy${ShiftAssignmentType.ALGORITHM}`,
  },
  [ShiftAssignmentType.EXCHANGED]: {
    borderColor: '#F59E0B', // amber-500
    backgroundColor: '#FFFBEB', // amber-50
    textColor: '#92400E', // amber-800
    translationKey: `generic.assignedBy${ShiftAssignmentType.EXCHANGED}`,
  },
};

function ByTypeShiftCard({
  shift_assignment,
  sectionSlotsDict,
  sectionsDict,
  users,
  importanceValues,
  setImportanceValues,
}: {
  shift_assignment: ShiftAssignment;
  sectionSlotsDict: Record<number, SectionSlot>;
  sectionsDict: Record<number, Section>;
  users: Record<number, User>;
  importanceValues: ComponentPrioritySchema;
  setImportanceValues: (
    fn: (prev: ComponentPrioritySchema) => ComponentPrioritySchema,
  ) => void;
}) {
  const { t } = useTranslation();
  const sectionSlot = sectionSlotsDict[shift_assignment.id_section_slot];
  const section = sectionsDict[sectionSlot?.id_section];
  const user = users[shift_assignment.id_user];

  return (
    <div
      key={shift_assignment.id_shift_assignment}
      className="grid grid-cols-[3fr,1fr,2fr] gap-x-4 items-center p-2 hover:bg-gray-50"
    >
      <div className="flex items-center gap-2">
        <div className="flex flex-col">
          <span className="text-sm font-medium flex items-center gap-2">
            {formatUserName(user, false)}
            <IconDisplay type={shift_assignment.type} />
            <div className="flex items-center gap-1">
              <span
                className="w-5 h-5 rounded-full"
                style={{ backgroundColor: returnColor(section?.color) }}
              />
              <span className="text-xs font-medium">{section?.acronym}</span>
            </div>
          </span>
          <div className="flex flex-col">
            <span className="text-sm text-gray-500">
              {dateToString(sectionSlot?.start, 'weekday-hour')} -{' '}
              {dateToString(sectionSlot?.end, 'weekday-hour')}
            </span>
          </div>
        </div>
      </div>
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-600">
            {t('generic.priority')}
            {': '}
          </span>
        </div>
      </div>
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-2">
          <ImportanceValueInput
            value={
              importanceValues[AlgComponentType.C_PREVIOUSLY_ASSIGNED_SHIFTS][
                shift_assignment.id_shift_assignment
              ] ?? 0
            }
            onChange={value => {
              const updatedValues: ComponentPrioritySchema = {
                ...importanceValues,
                [AlgComponentType.C_PREVIOUSLY_ASSIGNED_SHIFTS]: {
                  ...importanceValues[
                    AlgComponentType.C_PREVIOUSLY_ASSIGNED_SHIFTS
                  ],
                  [shift_assignment.id_shift_assignment]: value,
                },
              };
              setImportanceValues(() => updatedValues);
            }}
            isUnbreakable={isUnbreakable(
              importanceValues[AlgComponentType.C_PREVIOUSLY_ASSIGNED_SHIFTS][
                shift_assignment.id_shift_assignment
              ] ?? 0,
            )}
          />
          <ImportanceLevelListbox
            value={
              importanceValues[AlgComponentType.C_PREVIOUSLY_ASSIGNED_SHIFTS][
                shift_assignment.id_shift_assignment
              ] ?? 0
            }
            onChange={level => {
              const value = getImportanceValue(level);
              const bracket = getImportanceLevel(value);
              if (bracket) {
                const updatedValues: ComponentPrioritySchema = {
                  ...importanceValues,
                  [AlgComponentType.C_PREVIOUSLY_ASSIGNED_SHIFTS]: {
                    ...importanceValues[
                      AlgComponentType.C_PREVIOUSLY_ASSIGNED_SHIFTS
                    ],
                    [shift_assignment.id_shift_assignment]: value,
                  },
                };
                setImportanceValues(() => updatedValues);
              }
            }}
          />
        </div>
      </div>
    </div>
  );
}

interface PreassignedShiftPriorityByTypeProps {
  shiftAssignments: Record<number, ShiftAssignment>;
  users: Record<number, User>;
  importanceValues: ComponentPrioritySchema;
  setImportanceValues: (
    fn: (prev: ComponentPrioritySchema) => ComponentPrioritySchema,
  ) => void;
}

export default function PreassignedShiftPriorityByType({
  shiftAssignments,
  users,
  importanceValues,
  setImportanceValues,
}: PreassignedShiftPriorityByTypeProps) {
  const [expandedTypes, setExpandedTypes] = useState<Set<ShiftAssignmentType>>(
    new Set(),
  );
  const { t } = useTranslation();
  const { sectionSlotsDict, sectionsWithSlots } = useItrContext();

  // Create a dictionary of sections for quick lookup
  const sectionsDict = sectionsWithSlots.reduce(
    (acc, { section }) => {
      acc[section.id_section] = section;
      return acc;
    },
    {} as Record<number, Section>,
  );

  const computeTypeModeImportance = useMemo(() => {
    const typeModes: Record<ShiftAssignmentType, number> = {} as Record<
      ShiftAssignmentType,
      number
    >;

    // Group shifts by type
    const shiftsByType = Object.values(shiftAssignments).reduce(
      (acc, shift) => {
        if (!acc[shift.type]) {
          acc[shift.type] = [];
        }
        acc[shift.type].push(shift);
        return acc;
      },
      {} as Record<ShiftAssignmentType, ShiftAssignment[]>,
    );

    // Compute mode for each type
    Object.entries(shiftsByType).forEach(([type, shifts]) => {
      const importanceCounts: Record<number, number> = {};
      let maxCount = 0;
      let mode = 0;

      shifts.forEach(shift => {
        const importance =
          importanceValues[AlgComponentType.C_PREVIOUSLY_ASSIGNED_SHIFTS][
            shift.id_shift_assignment
          ] ?? 0;
        importanceCounts[importance] = (importanceCounts[importance] || 0) + 1;

        if (importanceCounts[importance] > maxCount) {
          maxCount = importanceCounts[importance];
          mode = importance;
        }
      });

      typeModes[type as ShiftAssignmentType] = mode;
    });

    return typeModes;
  }, [shiftAssignments, importanceValues]);

  const toggleType = (type: ShiftAssignmentType) => {
    setExpandedTypes(prev => {
      const newSet = new Set(prev);
      if (newSet.has(type)) {
        newSet.delete(type);
      } else {
        newSet.add(type);
      }
      return newSet;
    });
  };

  // Group shifts by type
  const shiftsByType = Object.values(shiftAssignments).reduce(
    (acc, shift) => {
      if (!acc[shift.type]) {
        acc[shift.type] = [];
      }
      acc[shift.type].push(shift);
      return acc;
    },
    {} as Record<ShiftAssignmentType, ShiftAssignment[]>,
  );

  // Create a new object with sorted shifts by type
  const sortedShiftsByType = Object.entries(shiftsByType).reduce(
    (acc, [type, shifts]) => {
      acc[type as ShiftAssignmentType] = [...shifts].sort((a, b) => {
        const userA = users[a.id_user];
        const userB = users[b.id_user];
        return userA.firstname.localeCompare(userB.firstname);
      });
      return acc;
    },
    {} as Record<ShiftAssignmentType, ShiftAssignment[]>,
  );

  return (
    <div className="space-y-4">
      {(
        Object.entries(sortedShiftsByType) as [
          ShiftAssignmentType,
          ShiftAssignment[],
        ][]
      ).map(([type, shifts]) => {
        const isExpanded = expandedTypes.has(type);
        const typeModeImportance = computeTypeModeImportance[type];

        return (
          <div key={type} className="border rounded">
            <button
              onClick={() => toggleType(type)}
              className="flex items-center justify-between w-full text-left p-2"
              style={{
                borderLeft: `4px solid ${SHIFT_TYPE_STYLES[type].borderColor}`,
                backgroundColor: SHIFT_TYPE_STYLES[type].backgroundColor,
                color: SHIFT_TYPE_STYLES[type].textColor,
              }}
            >
              <div className="flex items-center gap-2">
                <IconDisplay type={type} />
                <div className="font-medium">
                  {t('generic.assignedBy')}{' '}
                  {t(SHIFT_TYPE_STYLES[type].translationKey)}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <ImportanceValueInput
                  value={typeModeImportance}
                  onChange={value => {
                    const updatedValues: ComponentPrioritySchema = {
                      ...importanceValues,
                      [AlgComponentType.C_PREVIOUSLY_ASSIGNED_SHIFTS]: {
                        ...importanceValues[
                          AlgComponentType.C_PREVIOUSLY_ASSIGNED_SHIFTS
                        ],
                        ...shifts.reduce(
                          (acc, shift) => ({
                            ...acc,
                            [shift.id_shift_assignment]: value,
                          }),
                          {},
                        ),
                      },
                    };
                    setImportanceValues(() => updatedValues);
                  }}
                  isUnbreakable={isUnbreakable(typeModeImportance)}
                />
                <ImportanceLevelListbox
                  value={typeModeImportance}
                  onChange={level => {
                    const value = getImportanceValue(level);
                    const bracket = getImportanceLevel(value);
                    if (bracket) {
                      const updatedValues: ComponentPrioritySchema = {
                        ...importanceValues,
                        [AlgComponentType.C_PREVIOUSLY_ASSIGNED_SHIFTS]: {
                          ...importanceValues[
                            AlgComponentType.C_PREVIOUSLY_ASSIGNED_SHIFTS
                          ],
                          ...shifts.reduce(
                            (acc, shift) => ({
                              ...acc,
                              [shift.id_shift_assignment]: value,
                            }),
                            {},
                          ),
                        },
                      };
                      setImportanceValues(() => updatedValues);
                    }
                  }}
                />
                {isExpanded ? (
                  <ChevronUpIcon className="h-4 w-4" />
                ) : (
                  <ChevronDownIcon className="h-4 w-4" />
                )}
              </div>
            </button>
            {isExpanded && (
              <div className="p-2 space-y-2">
                {shifts.map(shift => (
                  <ByTypeShiftCard
                    key={shift.id_shift_assignment}
                    shift_assignment={shift}
                    sectionSlotsDict={sectionSlotsDict}
                    sectionsDict={sectionsDict}
                    users={users}
                    importanceValues={importanceValues}
                    setImportanceValues={setImportanceValues}
                  />
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
